import { Category as CategoryIcon, JoinInner as CategoryMappingIcon } from "@mui/icons-material";
import { FormControl, MenuItem, Select } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import { PageShell } from "@/components/common";
import { paths } from "@/routes/paths";

import CategoryMapping from "./CategoryMapping";
import CraveCategories from "./CraveCategories";
import CustomerCategory from "./CustomerCategory";
import { CategoryViewFiltersProvider } from "./CategoryViewFiltersContext";

type CategorySection = "crave-categories" | "customer-categories" | "category-mapping";

const CATEGORY_SECTIONS: CategorySection[] = [
  "crave-categories",
  "customer-categories",
  "category-mapping",
];

const CategoryPage: React.FC = () => {
  const { t } = useTranslation();
  const { section } = useParams<{ section: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const activeSection = section as CategorySection | undefined;

  if (!activeSection || !CATEGORY_SECTIONS.includes(activeSection)) {
    return (
      <Navigate
        to={{ pathname: `${paths.admin.categories}/customer-categories`, search: location.search }}
        replace
      />
    );
  }

  const content = {
    "crave-categories": <CraveCategories embedded />,
    "customer-categories": <CustomerCategory embedded />,
    "category-mapping": <CategoryMapping embedded />,
  }[activeSection];
  const headerIcon =
    activeSection === "category-mapping" ? <CategoryMappingIcon /> : <CategoryIcon />;

  return (
    <CategoryViewFiltersProvider>
      <PageShell
        breadcrumbs="admin"
        title={t("management.category.title")}
        icon={headerIcon}
        headerActions={
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              aria-label={t("management.category.fields.section")}
              value={activeSection}
              onChange={(event) => {
                const searchParams = new URLSearchParams(location.search);
                searchParams.delete("page");

                void navigate({
                  pathname: `${paths.admin.categories}/${event.target.value}`,
                  search: searchParams.toString(),
                });
              }}
            >
              <MenuItem value="crave-categories">
                {t("management.category.craveCategories.title")}
              </MenuItem>
              <MenuItem value="customer-categories">
                {t("management.category.customerCategory.sections.customerCategories")}
              </MenuItem>
              <MenuItem value="category-mapping">
                {t("management.category.categoryMapping.title")}
              </MenuItem>
            </Select>
          </FormControl>
        }
      >
        {content}
      </PageShell>
    </CategoryViewFiltersProvider>
  );
};

export default CategoryPage;
